import { emailRegex } from 'app/common/utils';
import { AccessConfiguration } from 'app/components/access-sharing';
import { SubmitButton, TextField } from 'app/components/form-fields';
import {
  BackActionContentLayout,
  CardContentLayout,
} from 'app/components/layouts';
import { ConfirmAccessPeriodSettingsDialog } from 'app/pages/AccessSharingPage/SelectUser/ConfirmAccessPeriodSettingsDialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IAccessSharingPeriodType,
  IAccessSharingPermissionType,
  IAccessSharingUserData,
} from 'types/IAccessSharingUser';
import './AccessSharingView.less';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { removeEmojis } from 'utils/general';

interface Props {
  selectedUsers: Map<string, IAccessSharingUserData>;
  editableUserEmail?: string;
  setSelectedUsers: (users: Map<string, IAccessSharingUserData>) => void;
  onBack: () => void;
  onNextStep: () => void;
}

// TODO validation isn't finished and will be fixed in next PRs
export default function AccessSharingView({
  selectedUsers,
  editableUserEmail,
  setSelectedUsers,
  onNextStep,
  onBack,
}: Props) {
  const { t } = useTranslation();

  const editableUser = selectedUsers.get(editableUserEmail ?? '');

  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState(editableUser?.email ?? '');
  const [role, setRole] = useState(
    editableUser?.role ?? IAccessSharingPermissionType.Viewer,
  );
  const [period, setPeriod] = useState(
    editableUser?.period ?? IAccessSharingPeriodType.Continuous,
  );

  const [startDate, setStartDate] = useState(editableUser?.startsAt ?? null);
  const [endDate, setEndDate] = useState(editableUser?.endsAt ?? null);
  const [startTime] = useState(editableUser?.startsAt ?? null);
  const [endTime] = useState(editableUser?.endsAt ?? null);

  const [
    showConfirmAccessPeriodSettingsDialog,
    setShowConfirmAccessPeriodSettingsDialog,
  ] = useState(false);

  function handleConfirmAccessPeriodSettingsDialog() {
    setShowConfirmAccessPeriodSettingsDialog(false);
    handleSubmit({ currentPeriod: IAccessSharingPeriodType.Continuous });
  }

  const handleSubmit = (options: {
    currentPeriod: IAccessSharingPeriodType;
  }) => {
    const { currentPeriod } = options;
    const startYear = startDate?.getFullYear() as number;
    const startMonth = startDate?.getMonth() as number;
    const startDay = startDate?.getDate() || 1;
    const startHours = startTime?.getHours() || 0;
    const startMinutes = startTime?.getMinutes() || 0;
    const endYear = endDate?.getFullYear() as number;
    const endMonth = endDate?.getMonth() as number;
    const endDay = endDate?.getDate() || 1;
    const endHours = endTime?.getHours() || 0;
    const endMinutes = endTime?.getMinutes() || 0;
    const isStartDateSet = startYear && startMonth;
    const isEndDateSet = endYear && endMonth;

    if (
      (!isStartDateSet || !isEndDateSet) &&
      currentPeriod === IAccessSharingPeriodType.Temporary
    ) {
      setShowConfirmAccessPeriodSettingsDialog(true);
      return;
    }

    const user = {
      email,
      role,
      period,
      startsAt:
        currentPeriod === IAccessSharingPeriodType.Temporary && isStartDateSet
          ? new Date(startYear, startMonth, startDay, startHours, startMinutes)
          : undefined,
      endsAt:
        currentPeriod === IAccessSharingPeriodType.Temporary && isEndDateSet
          ? new Date(endYear, endMonth, endDay, endHours, endMinutes)
          : undefined,
    };

    const updatedUsers = new Map(selectedUsers);
    updatedUsers.set(email, user);

    if (!!editableUserEmail && editableUserEmail !== email) {
      updatedUsers.delete(editableUserEmail);
    }

    setSelectedUsers(updatedUsers);

    onNextStep();
  };

  const handleOnChangeEmail = (event: InputChangeEvent) => {
    const email = removeEmojis(event.value as string);
    if (email.length > 128) return;
    setEmail(email);
    setEmailError('');
  };

  const validateEmail = () => {
    if (!email) {
      setEmailError(t('access_sharing.flows.validation.email_is_required'));
      return false;
    }

    if (!email.toLowerCase().match(emailRegex)) {
      setEmailError(t('incorrect_email_format'));
      return false;
    }

    return true;
  };

  return (
    <>
      <BackActionContentLayout onBack={onBack}>
        <CardContentLayout
          title={t('select_user_to_share_title')}
          subtitle={t('select_user_to_share_subtitle')}
        >
          <div className="access-sharing-view">
            <TextField
              label={t('email')}
              value={email}
              onChange={handleOnChangeEmail}
              required
              className="access-sharing-view__text-field"
              placeholder={t('what_is_their_email')}
              error={emailError}
            />

            <AccessConfiguration
              initialPermission={role}
              period={period}
              startDate={startDate}
              endDate={endDate}
              onChangePermission={setRole}
              onChangePeriod={setPeriod}
              onChangeStartDate={setStartDate}
              onChangeEndDate={setEndDate}
            />
          </div>

          <SubmitButton
            onClick={() => {
              if (validateEmail()) {
                handleSubmit({ currentPeriod: period });
              }
            }}
          >
            {t('continue_text')}
          </SubmitButton>
        </CardContentLayout>
      </BackActionContentLayout>

      {/* TODO refactor this dialog */}
      {showConfirmAccessPeriodSettingsDialog && (
        <ConfirmAccessPeriodSettingsDialog
          onCancel={() => setShowConfirmAccessPeriodSettingsDialog(false)}
          onContinue={handleConfirmAccessPeriodSettingsDialog}
        />
      )}
    </>
  );
}
